import { useEffect, useState } from 'react';
import { BillingEntry, BillingAuthStatus, Vendor, SOW, AuthEvent } from '../types';
import { billingStore, vendorStore, sowStore } from '../services/store';
import { useVendorRole } from '../hooks/useVendorRole';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const INPUT = 'w-full px-2.5 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#1a5c2a]/20 focus:border-[#1a5c2a]';

const STATUS_COLOR: Record<BillingAuthStatus, [string, string]> = {
  PendingDM: ['#E6F1FB', '#185FA5'],
  Approved:  ['#EAF3DE', '#3B6D11'],
  Rejected:  ['#FCEBEB', '#A32D2D'],
};

function StatusBadge({ status }: { status: BillingAuthStatus }) {
  const [bg, color] = STATUS_COLOR[status];
  return <span className="px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ background: bg, color }}>{status}</span>;
}

function fmt(n: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

// ─── Log entry modal ──────────────────────────────────────────────────────────

function LogEntryModal({ vendors, onSave, onCancel, userEmail }: {
  vendors: Vendor[]; onSave: () => void; onCancel: () => void; userEmail: string;
}) {
  const [form, setForm] = useState({
    vendorId:   '',
    sowId:      '',
    type:       'Invoice' as 'Invoice' | 'Milestone',
    amount:     '',
    description: '',
    invoiceRef: '',
    entryDate:  new Date().toISOString().split('T')[0],
  });

  const sows = form.vendorId ? sowStore.listByVendor(form.vendorId).filter(s => s.authStatus === 'Executed') : [];

  const save = () => {
    if (!form.vendorId) { toast.error('Select a vendor'); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    if (!form.description.trim()) { toast.error('Description is required'); return; }

    billingStore.create({
      vendorId:   form.vendorId,
      sowId:      form.sowId || undefined,
      type:       form.type,
      amount:     parseFloat(form.amount),
      description: form.description.trim(),
      invoiceRef: form.invoiceRef || undefined,
      entryDate:  form.entryDate,
      loggedBy:   userEmail,
      authStatus: 'PendingDM',
    });
    toast.success('Billing entry logged — pending DM approval');
    onSave();
  };

  return (
    <>
      <h2 className="text-[17px] font-semibold text-gray-900 mb-4">Log Billing Entry</h2>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Vendor *">
          <select className={INPUT} value={form.vendorId} onChange={e => setForm(f => ({ ...f, vendorId: e.target.value, sowId: '' }))}>
            <option value="">— Select vendor —</option>
            {vendors.filter(v => v.status === 'Active').map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </Field>
        <Field label="Linked SOW (optional)">
          <select className={INPUT} value={form.sowId} onChange={e => setForm(f => ({ ...f, sowId: e.target.value }))}>
            <option value="">— None —</option>
            {sows.map(s => <option key={s.id} value={s.id}>{s.sowNumber} — {s.name}</option>)}
          </select>
        </Field>
        <Field label="Type">
          <select className={INPUT} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value as never }))}>
            <option value="Invoice">Invoice</option>
            <option value="Milestone">Milestone</option>
          </select>
        </Field>
        <Field label="Amount ($) *">
          <input className={INPUT} type="number" min="0" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" />
        </Field>
        <Field label="Entry Date">
          <input className={INPUT} type="date" value={form.entryDate} onChange={e => setForm(f => ({ ...f, entryDate: e.target.value }))} />
        </Field>
        <Field label="Invoice / Reference #">
          <input className={INPUT} value={form.invoiceRef} onChange={e => setForm(f => ({ ...f, invoiceRef: e.target.value }))} placeholder="INV-2026-001" />
        </Field>
      </div>
      <Field label="Description *">
        <textarea className={`${INPUT} min-h-[60px] resize-y`} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
      </Field>
      <div className="flex justify-end gap-2.5 mt-4">
        <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm cursor-pointer">Cancel</button>
        <button onClick={save} className="px-4 py-2 rounded-lg bg-[#1a5c2a] text-white text-sm font-medium cursor-pointer hover:bg-[#154a22]">Log Entry</button>
      </div>
    </>
  );
}

// ─── Billing row ──────────────────────────────────────────────────────────────

function BillingRow({ entry, vendor, sow, canApprove, userEmail, onRefresh }: {
  entry: BillingEntry; vendor?: Vendor; sow?: SOW;
  canApprove: boolean; userEmail: string; onRefresh: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [showReject, setShowReject] = useState(false);

  const act = (newStatus: BillingAuthStatus, approvalStatus: 'Approved' | 'Rejected') => {
    const event: AuthEvent = {
      step:      'DMApproval',
      status:    approvalStatus,
      actor:     userEmail,
      timestamp: new Date().toISOString(),
      notes:     rejectNote || undefined,
    };
    billingStore.update(entry.id, {
      authStatus:  newStatus,
      authHistory: [...entry.authHistory, event],
    });
    if (newStatus === 'Approved') toast.success('Entry approved — spend updated');
    else toast.success('Entry rejected');
    setShowReject(false);
    onRefresh();
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-2">
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50" onClick={() => setExpanded(x => !x)}>
        <span className="text-[10px] text-gray-400 transition-transform duration-200 inline-block" style={{ transform: expanded ? 'rotate(90deg)' : 'none' }}>▶</span>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-[14px]">{vendor?.name ?? 'Unknown'}</span>
            <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{entry.type}</span>
            <StatusBadge status={entry.authStatus} />
          </div>
          <div className="flex gap-4 text-[11px] text-gray-400 mt-0.5 flex-wrap">
            <span>{entry.entryDate}</span>
            {entry.invoiceRef && <span>Ref: {entry.invoiceRef}</span>}
            {sow && <span>SOW: {sow.sowNumber}</span>}
            <span className="text-gray-600 font-medium">{fmt(entry.amount)}</span>
          </div>
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <p className="text-sm text-gray-700 mt-3 mb-2">{entry.description}</p>
          <p className="text-[11px] text-gray-400">Logged by: {entry.loggedBy} · {entry.createdAt.split('T')[0]}</p>

          {/* DM approval actions */}
          {entry.authStatus === 'PendingDM' && canApprove && (
            <div className="mt-3">
              {showReject ? (
                <div className="space-y-2">
                  <textarea
                    className={`${INPUT} min-h-[48px] resize-y`}
                    placeholder="Rejection reason (optional)…"
                    value={rejectNote}
                    onChange={e => setRejectNote(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <button onClick={() => act('Rejected', 'Rejected')} className="px-3 py-1.5 bg-[#FCEBEB] text-[#A32D2D] rounded-lg text-xs font-medium cursor-pointer hover:bg-[#F7C1C1]">
                      Confirm Rejection
                    </button>
                    <button onClick={() => setShowReject(false)} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs cursor-pointer">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => act('Approved', 'Approved')} className="px-3 py-1.5 bg-[#1a5c2a] text-white rounded-lg text-xs font-medium cursor-pointer hover:bg-[#154a22]">
                    Approve
                  </button>
                  <button onClick={() => setShowReject(true)} className="px-3 py-1.5 bg-[#FCEBEB] text-[#A32D2D] rounded-lg text-xs font-medium cursor-pointer hover:bg-[#F7C1C1]">
                    Reject
                  </button>
                </div>
              )}
            </div>
          )}

          {/* History */}
          {entry.authHistory.length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">History</div>
              {entry.authHistory.map((ev, i) => (
                <div key={i} className="text-[11px] text-gray-500 mb-0.5">
                  {ev.timestamp.split('T')[0]} · {ev.status} by {ev.actor}{ev.notes ? ` — ${ev.notes}` : ''}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function BillingPage() {
  const { role, canManageBilling, canApproveBilling, canSeeVendor } = useVendorRole();
  const { account } = useAuth();
  const userEmail = account?.username ?? '';

  const [entries, setEntries] = useState<BillingEntry[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [sows, setSows] = useState<SOW[]>([]);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterVendor, setFilterVendor] = useState<string>('');
  const [showModal, setShowModal] = useState(false);

  const load = () => {
    setEntries(billingStore.list().sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
    setVendors(vendorStore.list());
    setSows(sowStore.list());
  };

  useEffect(() => { load(); }, []);

  if (!canManageBilling) {
    return <div className="p-8 text-gray-500">You don't have access to the Billing module.</div>;
  }

  const vendorMap = new Map(vendors.map(v => [v.id, v]));
  const sowMap    = new Map(sows.map(s => [s.id, s]));

  const filtered = entries
    .filter(e => {
      const v = vendorMap.get(e.vendorId);
      return v ? canSeeVendor(v.categories) : false;
    })
    .filter(e => !filterStatus || e.authStatus === filterStatus)
    .filter(e => !filterVendor || e.vendorId === filterVendor);

  const totalApproved = filtered.filter(e => e.authStatus === 'Approved').reduce((s, e) => s + e.amount, 0);
  const pendingCount  = filtered.filter(e => e.authStatus === 'PendingDM').length;

  return (
    <div className="p-8">
      <div className="flex justify-between items-start mb-5">
        <div>
          <h1 className="text-[22px] font-semibold text-gray-900 mb-0.5">Billing</h1>
          <p className="text-sm text-gray-500">{filtered.length} entries · {fmt(totalApproved)} approved spend · {pendingCount} pending approval</p>
        </div>
        {role === 'VendorManager' && (
          <button
            onClick={() => setShowModal(true)}
            className="px-3 py-1.5 bg-[#1a5c2a] text-white rounded-lg text-sm font-medium cursor-pointer hover:bg-[#154a22]"
          >
            + Log Entry
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-2.5 mb-5">
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white outline-none"
        >
          <option value="">All statuses</option>
          <option value="PendingDM">Pending DM</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
        </select>
        <select
          value={filterVendor}
          onChange={e => setFilterVendor(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white outline-none"
        >
          <option value="">All vendors</option>
          {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
      </div>

      {/* Summary cards */}
      {filtered.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-5">
          {[
            { label: 'Total approved', value: fmt(totalApproved), color: '#3B6D11' },
            { label: 'Pending approval', value: String(pendingCount), color: '#185FA5' },
            { label: 'Total entries', value: String(filtered.length), color: '#5F5E5A' },
          ].map(c => (
            <div key={c.label} className="bg-white rounded-xl px-4 py-3 shadow-sm">
              <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{c.label}</div>
              <div className="text-2xl font-semibold mt-1" style={{ color: c.color }}>{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* List */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <div className="text-4xl mb-3">💰</div>
          <div className="text-sm">{entries.length === 0 ? 'No billing entries yet.' : 'No entries match your filters.'}</div>
        </div>
      ) : (
        filtered.map(entry => (
          <BillingRow
            key={entry.id}
            entry={entry}
            vendor={vendorMap.get(entry.vendorId)}
            sow={entry.sowId ? sowMap.get(entry.sowId) : undefined}
            canApprove={canApproveBilling}
            userEmail={userEmail}
            onRefresh={load}
          />
        ))
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-[540px] p-6" onClick={e => e.stopPropagation()}>
            <LogEntryModal
              vendors={vendors}
              userEmail={userEmail}
              onSave={() => { setShowModal(false); load(); }}
              onCancel={() => setShowModal(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
